import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/apiClient";

import { CategoryColorPicker } from "@/components/stammdaten/CategoryColorPicker";
import { RentalPeriodPicker } from "@/components/stammdaten/RentalPeriodPicker";
import { DEFAULT_PITCH_CATEGORY_COLOR } from "@/lib/pitchCategoryColors";
import type { RentalPeriod } from "@/lib/rentalPeriod";

const EMPTY_FORM: { name: string; color: string; sort_order: string; rental_period: RentalPeriod } =
  {
    name: "",
    color: DEFAULT_PITCH_CATEGORY_COLOR,
    sort_order: "0",
    rental_period: "night",
  };

export interface CreateCategoryModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateCategoryModal({ open, onClose, onCreated }: CreateCategoryModalProps) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setError(null);
    setSubmitting(false);
  }, [open]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/api/v1/pitch-categories", {
        name: form.name.trim(),
        color: form.color,
        sort_order: Number(form.sort_order) || 0,
        rental_period: form.rental_period,
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Anlegen");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Neue Kategorie"
      description="Platzkategorie anlegen"
      size="lg"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <label className="flex flex-col gap-1 text-sm">
          Name
          <input
            required
            autoFocus
            className="eh-input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Farbe
          <CategoryColorPicker
            value={form.color}
            onChange={(color) => setForm((f) => ({ ...f, color }))}
          />
        </label>
        <div className="space-y-2">
          <p className="text-sm">Abrechnungszeitraum</p>
          <RentalPeriodPicker
            value={form.rental_period}
            onChange={(rental_period) => setForm((f) => ({ ...f, rental_period }))}
          />
          <p className="text-xs text-label-secondary">
            Der spätere Stellplatzpreis gilt für diesen Zeitraum, zum Beispiel pro Nacht oder pro Jahr.
          </p>
        </div>
        <label className="flex flex-col gap-1 text-sm">
          Sortierung
          <input
            type="number"
            className="eh-input w-32"
            value={form.sort_order}
            onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
          />
        </label>
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Abbrechen
          </Button>
          <Button type="submit" loading={submitting}>
            Anlegen
          </Button>
        </div>
      </form>
    </Modal>
  );
}
